import { useCallback, useEffect, useState } from "react";
import { ApiError, sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { fetchCompanyDashboard, fetchCompanyHospitals } from "../api/companyApi";
import {
  mapCompanyDashboardResponse,
  type EnterpriseDashboardViewData,
} from "../api/mapCompanyDashboard";
import type { CompanyHospital } from "../api/types";
import { enterpriseDashboardData } from "../data/enterpriseDashboard";
import { getAuthToken } from "../auth";

export type EnterpriseDashboardFilters = {
  dateFrom: string;
  dateTo: string;
  hospitalId: string;
};

export function useEnterpriseDashboard(filters: EnterpriseDashboardFilters) {
  const [data, setData] = useState<EnterpriseDashboardViewData>(enterpriseDashboardData);
  const [hospitals, setHospitals] = useState<CompanyHospital[]>([]);
  const [periodLabel, setPeriodLabel] = useState("Historique complet");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const loadHospitals = useCallback(async () => {
    if (!getAuthToken()) return;
    try {
      const rows = await fetchCompanyHospitals();
      setHospitals(rows);
    } catch {
      // Liste hôpitaux optionnelle — le dashboard reste utilisable.
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setData(enterpriseDashboardData);
      setPeriodLabel("Historique complet");
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const response = await fetchCompanyDashboard({
        date_start: filters.dateFrom || undefined,
        date_end: filters.dateTo || undefined,
        hospital_id: filters.hospitalId ? Number(filters.hospitalId) : undefined,
      });
      setData(mapCompanyDashboardResponse(response));
      setPeriodLabel(response.period_label || "Historique complet");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(null);
        return;
      }
      setError(
        sanitizeApiErrorMessage(
          err instanceof Error ? err.message : "Impossible de charger le dashboard.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [filters.dateFrom, filters.dateTo, filters.hospitalId]);

  useEffect(() => {
    void loadHospitals();
  }, [loadHospitals]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  return {
    data,
    hospitals,
    periodLabel,
    loading,
    error,
    usingApi,
    reload: loadDashboard,
  };
}
